import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContextType, ToastMessage, ToastProviderProps } from './Toaster.types';
import { v4 as uuidv4 } from 'uuid';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, defaultDuration = 5000 }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const addToast = useCallback(
        (toast: Omit<ToastMessage, 'id'>) => {
            const id = uuidv4();
            const newToast: ToastMessage = {
                ...toast,
                id,
                duration: toast.duration || defaultDuration,
                autoClose: toast.autoClose !== false
            };

            setToasts((prevToasts) => [...prevToasts, newToast]);
        },
        [defaultDuration]
    );

    const removeToast = useCallback((id: string) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, []);

    const clearToasts = useCallback(() => {
        setToasts([]);
    }, []);

    return <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>{children}</ToastContext.Provider>;
};

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);

    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }

    return context;
};

// Hooks utilitaires pour les différents types de toasts
export const useSuccessToast = () => {
    const { addToast } = useToast();

    return useCallback(
        (message: string, options?: Partial<Omit<ToastMessage, 'id' | 'type' | 'message'>>) => {
            addToast({
                type: 'success',
                message,
                ...options
            });
        },
        [addToast]
    );
};

export const useErrorToast = () => {
    const { addToast } = useToast();

    return useCallback(
        (message: string, options?: Partial<Omit<ToastMessage, 'id' | 'type' | 'message'>>) => {
            addToast({
                type: 'error',
                message,
                ...options
            });
        },
        [addToast]
    );
};

export const useInfoToast = () => {
    const { addToast } = useToast();

    return useCallback(
        (message: string, options?: Partial<Omit<ToastMessage, 'id' | 'type' | 'message'>>) => {
            addToast({
                type: 'info',
                message,
                ...options
            });
        },
        [addToast]
    );
};

export const useWarningToast = () => {
    const { addToast } = useToast();

    return useCallback(
        (message: string, options?: Partial<Omit<ToastMessage, 'id' | 'type' | 'message'>>) => {
            addToast({
                type: 'warning',
                message,
                ...options
            });
        },
        [addToast]
    );
};

// Hook utilitaire pour les réponses API
export const useApiResponseToast = () => {
    const { addToast } = useToast();

    return useCallback(
        (response: { code: string; status: number; data: any }, successMessage?: string, errorMessage?: string) => {
            const isSuccess = response.status >= 200 && response.status < 300;

            addToast({
                type: isSuccess ? 'success' : 'error',
                message: isSuccess ? successMessage || `Opération réussie (${response.code})` : errorMessage || `Erreur: ${response.code}`,
                data: response.data
            });
        },
        [addToast]
    );
};
